import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import './index.css';
import App from './App';
import { AuthProvider } from './AuthContext';
import ErrorBoundary from './ErrorBoundary';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { isNativePlatform } from './nativeBridge';
import { installAuthExpiryInterceptor } from './apiInterceptor';
import { initMobileUI } from './mobile/mobileUI';
// Loaded after App (and therefore after App.css) so the mobile layer sits
// last in the cascade. Every rule inside is scoped to html.m-app, so this
// import is inert on desktop.
import './mobile/mobile.css';
import { initAutoScrollToFocusedInput } from './autoScrollToFocusedInput';
import { i18nReady } from './i18n';
import { initMonitoring } from './monitoring';
import { setWaitingRegistration, watchForUpdates, installControllerChangeReload } from './pwaUpdate';

// First, so an error anywhere in start-up is caught. Does nothing unless the
// build set REACT_APP_SENTRY_DSN.
initMonitoring();

// Patch window.fetch before anything can call it.
//
// This used to be installed from a useEffect inside AuthProvider. AuthProvider
// wraps <App />, and React runs child effects BEFORE parent effects -- so
// App's own mount effects fired against the unpatched fetch. The site-visit
// beacon is one of them, and it went out with no X-CSRF-Token header and no
// _csrf cookie (the interceptor is what fetches /api/csrf-token, so that
// request never happened either). The server rejected it with 403 "CSRF token
// invalid", App.js swallowed that in a .catch(() => {}), and sessionStorage
// was marked as logged regardless -- so it never retried. Visit analytics
// silently recorded nothing.
//
// Installing at import time means the patch is in place before any component
// renders, let alone runs an effect. installAuthExpiryInterceptor() guards on
// its own `installed` flag, so AuthProvider's call is now a harmless no-op.
installAuthExpiryInterceptor();

// Runs before the first render so a phone never paints a frame of desktop
// layout before the mobile layer engages.
initMobileUI();
initAutoScrollToFocusedInput();

// Marks the document when running inside the Capacitor shell so CSS can give
// the packaged app its own treatment without a JS branch at every call site.
// Set before the first render (rather than from an effect) so the app-only
// chrome is correct on the first paint instead of flashing the web layout.
if (isNativePlatform()) document.documentElement.classList.add('is-native-app');

const root = ReactDOM.createRoot(document.getElementById('root'));

// Translations other than English are their own chunk now, so hold the first
// render until the detected language has arrived -- otherwise a returning
// French or Arabic visitor gets a visible frame of English first. This settles
// immediately for English, and i18nReady swallows its own failures, so it can
// neither block the render nor stop it happening.
i18nReady.then(() => {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
});

// Cache the app shell and product images for fast repeat visits and
// basic offline support. Only active in production builds, and skipped
// inside the native app shell -- service workers aren't meaningful (and are
// unreliable) inside a packaged Capacitor WebView, plus this app already
// ships fully bundled there.
if (!isNativePlatform()) {
  // A newly-deployed build installs and then sits in "waiting" state:
  // service-worker.js listens for SKIP_WAITING and calls clientsClaim(), but
  // something has to tell it to take over. Until it does, the old worker serves
  // every asset from its cache -- which is why a refresh, even a hard one, was
  // never enough to pick up a deploy.
  //
  // This used to send SKIP_WAITING the instant a new build was found, which
  // fires `controllerchange` below and reloads the page. The update always won,
  // and could take the tab out from under someone mid-checkout.
  //
  // Now the waiting worker is handed to pwaUpdate.js and the app OFFERS it
  // (UpdateAppBanner). Nothing reloads until somebody presses Update. The
  // reload path is unchanged -- applyUpdate() posts the same SKIP_WAITING --
  // only the moment it starts has moved.
  serviceWorkerRegistration.register({
    onUpdate: (registration) => setWaitingRegistration(registration),
  });
  // A service worker is normally only re-checked on navigation, so an installed
  // PWA left open would not notice a deploy at all. This looks on resume and on
  // a slow timer, so "how do I get updates" has an answer that is not "close
  // the app and reopen it".
  watchForUpdates();
  // The whole reload policy lives in pwaUpdate.js, so the two paths that can
  // trigger one -- pressing Update here, and another tab applying it -- cannot
  // disagree or fire twice.
  installControllerChangeReload();
}
reportWebVitals();
